import type { Category } from '@/features/templates/api'
import type { Flow, Level, MissingItem } from './api'

/** Texto do fluxo de geração (RN-DOC-38, docs/styleguide/04-escrita.md). */

export const FLOW_TITLES: Record<Flow, string> = {
  procedure: 'Gerar documentos',
  standalone: 'Documentos avulsos',
}

export const FLOW_CRUMBS: Record<Flow, string> = {
  procedure: 'gerar documentos',
  standalone: 'documentos avulsos',
}

export const STEP_LABELS = [
  'Escolha os documentos',
  'Confira as informações',
  'Documentos gerados',
] as const

export const STEP_SUBTITLES: Record<Flow, [string, string]> = {
  procedure: [
    'A anamnese preenchida entra como base e preenche o documento.',
    'Vem da anamnese e do cadastro. Só o que faltar precisa ser digitado.',
  ],
  standalone: [
    'Termos que existem fora do fluxo de procedimento — quitação, intercorrência, renúncia.',
    'Vem do cadastro. Só o que os termos escolhidos pedem precisa ser digitado.',
  ],
}

export const CATEGORY_LABELS: Record<Category, string> = {
  service_contract: 'Contrato de prestação',
  model_patient_contract: 'Contrato de paciente modelo',
  informed_consent: 'Termo de consentimento',
  anamnesis: 'Anamnese',
  standalone_form: 'Termo avulso',
  refund: 'Devolução',
  general_contract: 'Contrato',
  other: 'Outro',
}

/**
 * Adicionar documentos a um atendimento já enviado (RN-DOC-57): os termos novos vão num envio próprio, com link e
 * protocolo próprios; contrato e anamnese ficam no atendimento original.
 */
export const ADDITION = {
  crumb: 'adicionar documentos',
  action: 'Adicionar documentos',
  termsTitle: 'Documentos para acrescentar',
  emptyTitle: 'Nada para acrescentar',
  emptyBody: 'Todos os termos ativos da clínica já estão neste atendimento.',
  draftTitle: 'Rascunho de documentos adicionados',
} as const

/** O subtítulo das etapas 1 e 2 do adicional, com a data do atendimento original. */
export function additionSubtitle(step: 1 | 2, originalDate: string) {
  return step === 1
    ? `Para o atendimento de ${originalDate}. Vão num envio próprio, com link e protocolo próprios; contrato e anamnese ficam no original.`
    : `Profissional, procedimentos e valor vêm do atendimento de ${originalDate}. Só o que faltar precisa ser digitado.`
}

/** "Documentos adicionados · 05/08/2026 14:30" e "ao atendimento de 03/08/2026" (aba Documentos, Assinaturas). */
export function additionTitle(generatedAt: string) {
  return `Documentos adicionados · ${generatedAt}`
}

export function additionOf(originalDate: string) {
  return `ao atendimento de ${originalDate}`
}

export const RESTRICTED_NOTE = 'Só a profissional responsável e a gestora abrem este documento.'

/** "2 DE 3 SELECIONADOS" (o eyebrow já é caixa alta). */
export function counterLabel(selected: number, total: number) {
  return `${selected} de ${total} selecionados`
}

/** "Avançar com 2" ou o motivo de não avançar, no rótulo (RN-DOC-38). */
export function advanceLabel(count: number) {
  return count === 0 ? 'Selecione um documento' : `Avançar com ${count}`
}

const COUNT_WORDS = [
  'Nenhum',
  'Um',
  'Dois',
  'Três',
  'Quatro',
  'Cinco',
  'Seis',
  'Sete',
  'Oito',
  'Nove',
  'Dez',
  'Onze',
  'Doze',
]

/** "Três documentos gerados para Amanda Lima." */
export function generatedSentence(count: number, patientName: string) {
  const word = COUNT_WORDS[count] ?? String(count)
  return count === 1
    ? `Um documento gerado para ${patientName}.`
    : `${word} documentos gerados para ${patientName}.`
}

/** "Enviar os 3 documentos" */
export function sendLabel(count: number) {
  return count === 1 ? 'Enviar o documento' : `Enviar os ${count} documentos`
}

/** "Gerar documentos" ou o motivo no rótulo: "Faltam 2 campos". */
export function generateLabel(missing: number, isRegeneration: boolean) {
  if (missing > 0) return missing === 1 ? 'Falta 1 campo' : `Faltam ${missing} campos`
  return isRegeneration ? 'Gerar novamente' : 'Gerar documentos'
}

export const RESOLVE_AT_LABELS: Record<MissingItem['resolveAt'], string> = {
  step1: 'na etapa 1',
  step2: 'nesta etapa',
  patient_record: 'no cadastro da paciente',
  clinic_profile: 'em Empresa',
  catalog: 'na administração',
}

/** O erro do texto livre vazio, igual à pendência da API (RN-DOC-52). */
export const FREE_TEXT_REQUIRED = 'Escreva o texto livre que entra no documento.'

/** A nota do bloco Texto livre: como ele entra e em quais documentos (RN-DOC-52). */
export function freeTextNote(names: string[]) {
  const list =
    names.length <= 1
      ? (names[0] ?? '')
      : `${names.slice(0, -1).join(', ')} e ${names[names.length - 1]}`
  const usedIn =
    names.length === 1 ? `Documento que o usa: ${list}.` : `Documentos que o usam: ${list}.`
  return `Entra como foi escrito, num parágrafo próprio. ${usedIn}`
}

/** O nível da etiqueta de um documento, no vocabulário do StatusTag. */
export function statusLevel(level: Level): 'critical' | 'attention' | 'waiting' | 'done' {
  return level
}

/** "3 páginas" · "1 página" */
export function pagesLabel(pageCount: number | null) {
  if (pageCount === null) return null
  return pageCount === 1 ? '1 página' : `${pageCount} páginas`
}
